// ============================================================================
// ESC/POS — termoprinter uchun baytlar (58 mm = 32 belgi, 80 mm = 48 belgi).
//
// Sof funksiyalar (React'siz, brauzersiz) — escpos.test.ts da testlanadi.
// Matn printer tushunadigan bitta baytli belgilarga aylantiriladi:
//   'ascii' — lotin harflari; kirill matni lotinga o'giriladi (har qanday printer chiqaradi)
//   'cp866' — kirill harflari o'zicha (printerda PC866 kod sahifasi bo'lishi kerak, ESC t 17)
// ============================================================================

import { fmtDateTime, fmtNumber } from '@onepos/shared';
import { ORDER_TYPE_LABEL, fmtKg, fmtWeight, orderNo, payMethodLabel, readReceiptDesign } from './logic';
import type { Boot, KassaSettings, LineStyle, LocalOrder, OrderItemComponent, OrderItemJson, ReceiptDesign, ReceiptGroup } from './logic';
import { LOGO_WIDTH, MAX_H, padCenter, rasterBytes, scaleMono } from './bitmap';
import type { MonoBitmap } from './bitmap';

export type Codepage = 'ascii' | 'cp866';
export type Paper = 58 | 80;

export interface EscOptions {
  paper: Paper;
  codepage: Codepage;
}

export function colsFor(paper: Paper): number {
  return paper === 58 ? 32 : 48;
}

// ---------------------------------------------------------------------------
// Matn: tinish belgilari, kirill
// ---------------------------------------------------------------------------
const PUNCT: Record<string, string> = {
  '‘': "'", '’': "'", 'ʻ': "'", 'ʼ': "'", '`': "'", '´': "'", 'ʹ': "'",
  '“': '"', '”': '"', '«': '"', '»': '"', '„': '"',
  '–': '-', '—': '-', '−': '-', '‐': '-',
  '×': 'x', '…': '...', '№': 'N', '•': '*', '·': '.',
  ' ': ' ', ' ': ' ', ' ': ' ', '\t': ' ',
};

const CYR_LAT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'j', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l',
  м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'x', ц: 'ts', ч: 'ch', ш: 'sh',
  щ: 'sh', ъ: "'", ы: 'i', ь: '', э: 'e', ю: 'yu', я: 'ya', ў: "o'", қ: 'q', ғ: "g'", ҳ: 'h', і: 'i', є: 'ye',
};

// CP866: А..П 0x80, Р..Я 0x90, а..п 0xA0, р..я 0xE0, Ё F0, ё F1, Ў F6, ў F7
function cp866(ch: string): number | null {
  const c = ch.charCodeAt(0);
  if (c >= 0x410 && c <= 0x42f) return 0x80 + (c - 0x410);
  if (c >= 0x430 && c <= 0x43f) return 0xa0 + (c - 0x430);
  if (c >= 0x440 && c <= 0x44f) return 0xe0 + (c - 0x440);
  if (ch === 'Ё') return 0xf0;
  if (ch === 'ё') return 0xf1;
  if (ch === 'Ў') return 0xf6;
  if (ch === 'ў') return 0xf7;
  return null;
}
// O'zbek kirill harflari CP866 da yo'q — eng yaqin harf
const CP866_NEAR: Record<string, string> = { қ: 'к', Қ: 'К', ғ: 'г', Ғ: 'Г', ҳ: 'х', Ҳ: 'Х' };

function translitChar(ch: string): string {
  const low = ch.toLowerCase();
  const t = CYR_LAT[low];
  if (t === undefined) return '?';
  if (ch === low || t === '') return t;
  return t.length > 1 && t[1] !== "'" ? t[0]!.toUpperCase() + t.slice(1) : t.toUpperCase();
}

/**
 * Matnni printer belgilariga tayyorlaydi: natijadagi har bir belgi — bitta bayt (kenglik hisobi aniq bo'ladi).
 */
export function prepText(s: string, codepage: Codepage): string {
  let out = '';
  for (const ch of s.normalize('NFC')) {
    const mapped = PUNCT[ch];
    if (mapped !== undefined) {
      out += mapped;
      continue;
    }
    const c = ch.codePointAt(0) ?? 0x3f;
    if (c > 0xffff) out += '?';
    else if (c >= 0x20 && c < 0x7f) out += ch;
    else if (c === 0x0a) out += ' ';
    else if (c >= 0x400 && c <= 0x4ff) {
      if (codepage === 'cp866') {
        const near = CP866_NEAR[ch] ?? ch;
        out += cp866(near) !== null ? near : translitChar(ch);
      } else out += translitChar(ch);
    } else if (c < 0x20) continue;
    else {
      // Lotin harflari urg'u bilan: é -> e
      const base = ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
      out += base.length === 1 && base.charCodeAt(0) < 0x7f ? base : '?';
    }
  }
  return out;
}

function encodeChar(ch: string): number {
  const c = ch.charCodeAt(0);
  if (c < 0x80) return c;
  return cp866(ch) ?? 0x3f;
}

/** Bo'sh joydan bo'lib qatorlarga ajratish (tayyorlangan matn uchun) */
export function wrap(s: string, cols: number): string[] {
  const words = s.split(' ').filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (let w of words) {
    while (w.length > cols) {
      if (cur) {
        lines.push(cur);
        cur = '';
      }
      lines.push(w.slice(0, cols));
      w = w.slice(cols);
    }
    if (!w) continue;
    if (!cur) cur = w;
    else if (cur.length + 1 + w.length <= cols) cur += ' ' + w;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur || lines.length === 0) lines.push(cur);
  return lines;
}

/** Chap matn va o'ng summa bir qatorda; sig'masa chap matn bo'linadi */
export function row(left: string, right: string, cols: number): string[] {
  if (left.length + 1 + right.length <= cols) return [left + ' '.repeat(cols - left.length - right.length) + right];
  const lines = wrap(left, cols);
  const last = lines[lines.length - 1]!;
  if (last.length + 1 + right.length <= cols) {
    lines[lines.length - 1] = last + ' '.repeat(cols - last.length - right.length) + right;
  } else lines.push(' '.repeat(Math.max(0, cols - right.length)) + right.slice(-cols));
  return lines;
}

// ---------------------------------------------------------------------------
// Buyruqlar
// ---------------------------------------------------------------------------
const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

export class EscBuilder {
  private out: number[] = [];
  readonly cols: number;
  private size = { w: 1, h: 1 };

  constructor(readonly opts: EscOptions) {
    this.cols = colsFor(opts.paper);
    this.out.push(ESC, 0x40); // ESC @ — boshlang'ich holat
    if (opts.codepage === 'cp866') this.out.push(ESC, 0x74, 17); // ESC t 17 — PC866
  }

  /** Joriy shrift kengligida bir qatorga sig'adigan belgilar */
  get width(): number {
    return Math.floor(this.cols / this.size.w);
  }

  raw(...bytes: number[]): this {
    this.out.push(...bytes);
    return this;
  }
  align(a: 'left' | 'center' | 'right'): this {
    return this.raw(ESC, 0x61, a === 'left' ? 0 : a === 'center' ? 1 : 2);
  }
  bold(on: boolean): this {
    return this.raw(ESC, 0x45, on ? 1 : 0);
  }
  /** Shrift o'lchami: 1..2 marta keng/baland */
  font(w: 1 | 2 = 1, h: 1 | 2 = 1): this {
    this.size = { w, h };
    return this.raw(GS, 0x21, ((w - 1) << 4) | (h - 1));
  }
  /** Tayyor (prepText'dan o'tgan) qator */
  private line(s: string): this {
    for (const ch of s) this.out.push(encodeChar(ch));
    this.out.push(LF);
    return this;
  }
  /** Oddiy matn — bo'lib chiqariladi */
  text(s: string): this {
    for (const l of wrap(prepText(s, this.opts.codepage), this.width)) this.line(l);
    return this;
  }
  /** Chapda nom, o'ngda summa */
  pair(left: string, right: string): this {
    const cp = this.opts.codepage;
    for (const l of row(prepText(left, cp), prepText(right, cp), this.width)) this.line(l);
    return this;
  }
  /** Chekinish bilan (modifikatorlar) */
  indent(s: string, by = 2): this {
    const pad = ' '.repeat(by);
    for (const l of wrap(prepText(s, this.opts.codepage), this.width - by)) this.line(pad + l);
    return this;
  }
  rule(ch = '-'): this {
    return this.line(ch.repeat(this.width));
  }
  feed(n = 1): this {
    return this.raw(ESC, 0x64, n);
  }
  /** Qog'ozni surib qisman kesish (GS V 66 n) */
  cut(): this {
    return this.raw(GS, 0x56, 66, 3);
  }
  /** Oq-qora rasm qog'oz markazida (GS v 0, Q-068) */
  image(b: MonoBitmap): this {
    const dots = this.cols * 12;
    for (const x of rasterBytes(padCenter(b, dots))) this.out.push(x);
    return this;
  }
  /** Pul qutisini ochish (ESC p 0 25 250) */
  drawer(): this {
    return this.raw(ESC, 0x70, 0, 25, 250);
  }
  bytes(): Uint8Array {
    return Uint8Array.from(this.out);
  }
}

export function concatBytes(parts: readonly Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((s, p) => s + p.length, 0));
  let i = 0;
  for (const p of parts) {
    out.set(p, i);
    i += p.length;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Chek, oshxona cheki, sinov
// ---------------------------------------------------------------------------
type ReceiptOrder = Pick<
  LocalOrder,
  | 'daily_number' | 'local_no' | 'created_at_local' | 'order_type' | 'channel_name' | 'external_no' | 'table_name' | 'staff_name' | 'items' | 'subtotal'
  | 'discount_amount' | 'service_charge' | 'rounding' | 'total' | 'payments' | 'is_paid'
>;

export function hasCash(order: Pick<LocalOrder, 'payments'>): boolean {
  return order.payments.some((p) => p.method === 'cash' && p.amount > 0);
}

/** Yozuv kattaligi: oddiy, baland (2 baravar baland), katta (2 baravar keng va baland) */
const SIZE_FONT: Record<1 | 2 | 3, [1 | 2, 1 | 2]> = { 1: [1, 1], 2: [1, 2], 3: [2, 2] };

/** Chek oxiridagi platforma imzosi (Q-032). Restoran sozlamada o'chira oladi. */
export const RECEIPT_SIGN = 'ONE POS — onepos.uz';

export function receiptBytes(
  order: ReceiptOrder,
  boot: { tenant: Pick<Boot['tenant'], 'name'>; branch: Pick<Boot['branch'], 'name' | 'address' | 'phone'> },
  settings: Pick<KassaSettings, 'receiptHeader' | 'receiptFooter' | 'receiptBrand'> & { receipt?: ReceiptDesign },
  opts: EscOptions & {
    drawer?: boolean;
    bill?: boolean;
    /** "Nomdan keyingi matn" belgisi bilan — oldindan chizilgan rasm (printer.ts, brauzerda; Q-068) */
    headerBlock?: MonoBitmap | null;
    /** "Tepadagi xabar" belgisi bilan — oldindan chizilgan rasm (Q-069) */
    topBlock?: MonoBitmap | null;
  },
): Uint8Array {
  const b = new EscBuilder(opts);
  if (opts.drawer) b.drawer();
  const change = order.payments.reduce((s, p) => s + (p.change_amount ?? 0), 0);

  const d = settings.receipt ?? readReceiptDesign({});
  // Eski saqlangan ko'rinish (styles'siz) — standart guruhlar, nom — o'z sozlamasidan
  const st: Record<ReceiptGroup, LineStyle> = { ...readReceiptDesign({}).styles, ...d.styles, name: { size: d.nameSize, bold: d.nameBold } };
  /** Guruh yozuvlari: kattalik va qalinlik qo'yiladi, keyin oddiy holatga qaytadi */
  const group = (g: ReceiptGroup, draw: () => void) => {
    const [w, h] = SIZE_FONT[st[g].size];
    b.bold(st[g].bold).font(w, h);
    draw();
    b.font().bold(false);
  };

  b.align('center');
  // Logotip (Q-068): tanlangan kattalikda, qog'oz markazida
  if (d.logo) {
    const w = LOGO_WIDTH[d.logoSize ?? 'M'];
    b.image(scaleMono(d.logo, w, Math.min(MAX_H, Math.round((d.logo.h * w) / d.logo.w)))).feed(1);
  }
  if (d.top && opts.topBlock) b.image(opts.topBlock);
  else if (d.top) group('top', () => d.top.split('\n').forEach((l) => b.text(l)));
  group('name', () => b.text(boot.tenant.name));
  group('contacts', () => {
    if (boot.branch.name && boot.branch.name !== boot.tenant.name) b.text(boot.branch.name);
    if (d.showContacts && boot.branch.address) b.text(boot.branch.address);
    if (d.showPhone !== false && boot.branch.phone) b.text(boot.branch.phone);
    if (settings.receiptHeader && opts.headerBlock) b.image(opts.headerBlock);
    else if (settings.receiptHeader) for (const l of settings.receiptHeader.split('\n')) b.text(l);
  });
  b.align('left').rule();
  // Hisob cheki (prechek, Q-062): to'lovdan oldin mehmonga
  if (opts.bill) b.align('center').bold(true).font(1, 2).text('HISOB CHEKI').font().bold(false).align('left').rule();

  if (d.showNo) group('no', () => b.pair('Buyurtma', orderNo(order)));
  group('info', () => {
    if (d.showTime) b.pair(fmtDateTime(order.created_at_local), ORDER_TYPE_LABEL[order.order_type]);
    if (order.channel_name) b.pair(order.channel_name, order.external_no ? `#${order.external_no}` : '');
    else b.text(ORDER_TYPE_LABEL[order.order_type]);
    if (order.table_name) b.text(`Stol: ${order.table_name}`);
    if (d.showStaff && order.staff_name) b.text(`Kassir: ${order.staff_name}`);
  });
  b.rule();

  group('items', () => {
    for (const it of order.items.filter((i) => i.status !== 'voided')) {
      b.text(it.name);
      for (const m of it.modifiers) b.indent(`+ ${m.name}`);
      for (const c of it.components ?? []) b.indent(`- ${componentText(c)}`);
      // Kilogrammda (Q-072): "0,235 kg x 85 000/kg"
      if (it.weight_g) b.pair(`  ${fmtKg(it.weight_g)} x ${fmtNumber(it.kg_price ?? 0)}`, fmtNumber(it.line_total));
      else b.pair(`  ${fmtQty(it.qty)} x ${fmtNumber(it.unit_price + it.modifiers_price)}`, fmtNumber(it.line_total));
    }
  });
  b.rule();

  group('sums', () => {
    if (order.discount_amount > 0 || order.service_charge > 0 || order.rounding !== 0) b.pair('Jami', fmtNumber(order.subtotal));
    if (order.discount_amount > 0) b.pair('Chegirma', `-${fmtNumber(order.discount_amount)}`);
    if (order.service_charge > 0) b.pair('Xizmat haqi', fmtNumber(order.service_charge));
    if (order.rounding !== 0) b.pair('Yaxlitlash', fmtNumber(order.rounding));
  });
  group('total', () => b.pair(opts.bill ? 'TO‘LASH UCHUN' : 'TO‘LOV', fmtNumber(order.total)));
  if (opts.bill) {
    b.align('center').text('Bu to‘lov cheki emas').align('left');
  } else {
    group('sums', () => {
      for (const p of order.payments) b.pair(payMethodLabel(p.method), fmtNumber(p.cash_received ?? p.amount));
      if (change > 0) b.pair('Qaytim', fmtNumber(change));
    });
    if (!order.is_paid) b.align('center').bold(true).text('TO‘LANMAGAN').bold(false).align('left');
  }
  b.rule();
  if (settings.receiptFooter) {
    b.align('center');
    group('footer', () => settings.receiptFooter.split('\n').forEach((l) => b.text(l)));
    b.align('left');
  }
  if (settings.receiptBrand) b.align('center').text(RECEIPT_SIGN).align('left');
  return b.feed(3).cut().bytes();
}

/** Kombo tarkibi qatori: "2 x Lavash mol go‘shtli + Pishloq" (soni 1 bo'lsa — faqat nom) */
export function componentText(c: Pick<OrderItemComponent, 'name' | 'qty' | 'modifiers'>): string {
  return `${c.qty > 1 ? `${fmtQty(c.qty)} x ` : ''}${c.name}${c.modifiers.length ? ` + ${c.modifiers.join(', ')}` : ''}`;
}

function fmtQty(q: number): string {
  return Number.isInteger(q) ? String(q) : String(q).replace('.', ',');
}

/** Oshxona cheki: narxsiz, katta harflar bilan. extra — mavjud buyurtmaga qo'shilgan taomlar. */
export function kitchenBytes(
  order: Pick<LocalOrder, 'daily_number' | 'local_no' | 'created_at_local' | 'order_type' | 'table_name' | 'staff_name' | 'note'>,
  items: readonly (Pick<OrderItemJson, 'name' | 'qty' | 'modifiers' | 'note' | 'components'> & Partial<Pick<OrderItemJson, 'weight_g'>>)[],
  opts: EscOptions & { extra?: boolean; title?: string },
): Uint8Array {
  const b = new EscBuilder(opts);
  b.align('center').bold(true).font(2, 2).text(orderNo(order)).font(1, 1);
  b.text(opts.title ?? (opts.extra ? 'QO‘SHIMCHA' : 'OSHXONA')).bold(false).align('left');
  b.pair(fmtDateTime(order.created_at_local), ORDER_TYPE_LABEL[order.order_type]);
  if (order.table_name) b.bold(true).text(`Stol: ${order.table_name}`).bold(false);
  if (order.staff_name) b.text(`Kassir: ${order.staff_name}`);
  b.rule('=');
  b.bold(true).font(1, 2);
  for (const it of items) {
    b.text(it.weight_g ? `${fmtWeight(it.weight_g)} ${it.name}` : `${fmtQty(it.qty)} x ${it.name}`);
    for (const m of it.modifiers) b.indent(`+ ${m.name}`, 4);
    // Kombo: oshxona nimani tayyorlashini aniq ko'rsin
    for (const c of it.components ?? []) {
      b.indent(`${fmtQty(c.qty)} x ${c.name}`, 4);
      for (const m of c.modifiers) b.indent(`+ ${m}`, 6);
    }
    if (it.note) b.indent(`! ${it.note}`, 4);
  }
  b.font().bold(false);
  if (order.note) b.rule().text(`Izoh: ${order.note}`);
  return b.feed(3).cut().bytes();
}

/** Qaytarish cheki (Q-077): qaysi buyurtma, qaysi taomlar, qancha, qanday, nega */
export interface RefundSlip {
  order: Pick<LocalOrder, 'daily_number' | 'local_no' | 'created_at_local' | 'order_type' | 'table_name'>;
  tenant: string;
  at: string;
  staff: string | null;
  amount: number;
  method: string;
  reason: string;
  items: { name: string; qty: number; amount: number }[];
}

export function refundBytes(s: RefundSlip, opts: EscOptions): Uint8Array {
  const b = new EscBuilder(opts);
  b.align('center').bold(true).text(s.tenant).font(1, 2).text('QAYTARISH').font().bold(false).align('left');
  b.pair(`Buyurtma ${orderNo(s.order)}`, fmtDateTime(s.order.created_at_local));
  b.pair('Qaytarildi', fmtDateTime(s.at));
  if (s.staff) b.pair('Xodim', s.staff);
  b.rule();
  for (const it of s.items) b.pair(`${fmtNumber(it.qty)} x ${it.name}`, fmtNumber(it.amount));
  if (s.items.length) b.rule();
  b.bold(true).pair('QAYTARILDI', fmtNumber(s.amount)).bold(false);
  b.pair('Usul', payMethodLabel(s.method));
  if (s.reason) b.text(`Sabab: ${s.reason}`);
  b.feed(1).text('Imzo: ______________');
  return b.feed(3).cut().bytes();
}

export function testBytes(opts: EscOptions, info: { tenant: string; device: string; at: string }): Uint8Array {
  const b = new EscBuilder(opts);
  b.align('center').bold(true).font(1, 2).text('ONE POS').font().text('Sinov cheki').bold(false).align('left').rule();
  b.pair('Restoran', info.tenant).pair('Qurilma', info.device).pair('Vaqt', fmtDateTime(info.at));
  b.pair('Qog‘oz', `${opts.paper} mm (${b.cols} belgi)`);
  b.pair('Kodlash', opts.codepage === 'cp866' ? 'Kirill (PC866)' : 'Lotin');
  b.rule();
  b.text('1234567890'.repeat(Math.ceil(b.cols / 10)).slice(0, b.cols));
  b.text('O‘zbekcha: g‘alla, o‘rik, choy');
  b.text('Кирилл: Шаурма, Лаваш, ўқғҳ');
  b.bold(true).text('Qalin matn').bold(false);
  b.font(1, 2).text('Baland matn').font();
  b.rule();
  b.align('center').text('Chiroyli chiqqan bo‘lsa — sozlash tugadi.');
  return b.feed(3).cut().bytes();
}
